import { Box, Container, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AssignmentOutlined from '@mui/icons-material/AssignmentOutlined';
import { useTextStyles } from '../../../../ui/theme/text';
import { useSettingsEditorPanelStyles } from './settings-editor-panel.styles';

import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { Snackbar } from '../../../../ui/components/pure/snackbar/snackbar.component';
import { DynamicForm } from '../../forms/form.types';
import { Colors } from '../../../../ui/theme/colors';
import { Alert, AlertTitle } from '@mui/material';
import { SettingsFormState } from '../../reducer/settings-tree-reducer.types';
import { transformErrors } from '../../forms/dynamic-form/error-transform';
import { LoadingIndicator } from '../../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { TextareaWidget } from '../../forms/shared/textarea-widget';
import { ShrinkLabelBaseInputTemplate } from '../../forms/shared/shrink-label-base-input-template';

export interface SettingsDetailsPanelProps {
  dynamicForm: DynamicForm;
  currentFormState: SettingsFormState;
}

export function SettingsEditorPanel(props: SettingsDetailsPanelProps) {
  const classes = useSettingsEditorPanelStyles();
  const textClasses = useTextStyles();
  // If the form is readonly it cannot be saved
  const isReadOnly = props.currentFormState.readOnly === true || props.dynamicForm.formSchema.readOnly === true;
  const hasFormFields = Object.keys(props.dynamicForm.formSchema?.properties as object).length > 0;

  return (
    <>
      <Paper
        className={
          props.dynamicForm.componentToRenderBelowForm ||
          props.dynamicForm.componentToRenderInsideCard ||
          props.dynamicForm.renderInsideCard
            ? classes.paperCompact
            : classes.paper
        }
      >
        <Box className={classes.title}>
          <div className={classes.titleText}>
            <div className={classes.titleRow}>
              <Typography className={textClasses.h2}>{props.dynamicForm.title}</Typography>
              {props.dynamicForm.hasChanged && (
                <Typography className={classes.titleSubText} color="secondary">
                  Unsaved changes
                </Typography>
              )}
            </div>
            {props.dynamicForm.subtitle && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" style={{ color: '#888' }}>
                  {props.dynamicForm.subtitle}
                </Typography>
                {props.dynamicForm.allowSubtitleCopy && (
                  <Tooltip title="Copy ID">
                    <IconButton
                      size="small"
                      style={{ marginLeft: '4px', padding: '2px' }}
                      onClick={() => navigator.clipboard.writeText(props.dynamicForm.subtitle!)}
                    >
                      <AssignmentOutlined style={{ fontSize: '16px', color: '#888' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </div>
          {!isReadOnly && (
            <div>
              {props.currentFormState.formType === 'edit' && props.dynamicForm.hasChanged && (
                <TextButton onClick={props.dynamicForm.onReset} color="secondary" variant="outlined">
                  {props.dynamicForm.resetButtonLabel ?? 'Reset'}
                </TextButton>
              )}
              {props.currentFormState.formType === 'create' && (
                <TextButton onClick={props.dynamicForm.onReset} color="secondary" variant="outlined">
                  {props.dynamicForm.resetButtonLabel ?? 'Cancel'}
                </TextButton>
              )}

              <TextButton
                className={classes.saveButton}
                isLoading={props.currentFormState.isSubmitting}
                disabled={props.currentFormState.isSubmitting || !props.dynamicForm.hasChanged}
                color="primary"
                variant="contained"
                {...(hasFormFields
                  ? { type: 'submit' as const, form: 'settings-form' }
                  : {
                      onClick: () => {
                        props.dynamicForm.onSubmit(props.dynamicForm.defaultValues);
                      }
                    })}
                style={{
                  color: '#ffffff',
                  backgroundColor: props.dynamicForm.hasChanged
                    ? Colors.collectedGreen
                    : Colors.bookedScannedInProgressGray,
                  marginLeft: '8px'
                }}
              >
                Save Settings
              </TextButton>
            </div>
          )}
        </Box>
        {(props.currentFormState.error || props.dynamicForm.isLocked) && (
          <Container>
            {props.currentFormState.error && (
              <p>
                <Alert variant="outlined" severity="error">
                  <AlertTitle>Something went wrong</AlertTitle>
                  {props.currentFormState.error}
                </Alert>
              </p>
            )}
            {props.dynamicForm.isLocked && (
              <p style={{ marginTop: '16px' }}>
                <Alert variant="outlined" severity="warning">
                  <AlertTitle>Action Required</AlertTitle>
                  {props.dynamicForm.lockedMessage}
                </Alert>
              </p>
            )}
          </Container>
        )}
        {hasFormFields && (
          <Container>
            {props.dynamicForm.isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px'
                }}
              >
                <LoadingIndicator />
              </Box>
            )}
            {!props.dynamicForm.isLoading && !props.dynamicForm.isLocked && (
              <Form
                id="settings-form"
                noHtml5Validate
                showErrorList={false}
                transformErrors={transformErrors}
                formData={props.dynamicForm.defaultValues}
                schema={props.dynamicForm.formSchema}
                uiSchema={props.dynamicForm.uiSchema}
                fields={props.dynamicForm.fields}
                widgets={{ TextareaWidget }}
                templates={{ BaseInputTemplate: ShrinkLabelBaseInputTemplate }}
                formContext={props.dynamicForm.formContext}
                validator={validator}
                customValidate={props.dynamicForm.customValidate}
                onChange={({ formData }) => {
                  props.dynamicForm.onChange(formData);
                }}
                onSubmit={({ formData }) => {
                  props.dynamicForm.onSubmit(formData);
                }}
              />
            )}
          </Container>
        )}
        {props.dynamicForm.componentToRenderInsideCard && (
          <props.dynamicForm.componentToRenderInsideCard formData={props.dynamicForm.defaultValues} />
        )}
        {props.dynamicForm.renderInsideCard?.(props.dynamicForm.defaultValues, {
          onChange: props.dynamicForm.onChange,
          onReset: props.dynamicForm.onReset
        })}
        {props.currentFormState.isSuccess && (
          <Snackbar severity="success" onClose={props.dynamicForm.onAcknowledgeSuccess}>
            Form updated successfully
          </Snackbar>
        )}
      </Paper>
      {props.dynamicForm.componentToRenderBelowForm && (
        <props.dynamicForm.componentToRenderBelowForm formData={props.dynamicForm.defaultValues} />
      )}
    </>
  );
}
